import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
  type User as FirebaseUser,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from './config';
import type { User, UserRole } from '@/types';

const googleProvider = new GoogleAuthProvider();

// ─── Sign In ──────────────────────────────────────────────────────────────────

export async function signInWithEmail(email: string, password: string) {
  return signInWithEmailAndPassword(auth, email, password);
}

export async function signInWithGoogle() {
  const result = await signInWithPopup(auth, googleProvider);
  // Ensure user document exists in Firestore
  await ensureUserDocument(result.user, []);
  return result;
}

// ─── Sign Up ──────────────────────────────────────────────────────────────────

export async function signUpWithEmail(
  email: string,
  password: string,
  displayName: string,
  orgId: string | null = null,
  roles: UserRole[] = [],
) {
  const credential = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(credential.user, { displayName });
  await sendEmailVerification(credential.user);
  await ensureUserDocument(credential.user, roles, orgId, displayName);
  return credential;
}

// ─── Sign Out ─────────────────────────────────────────────────────────────────

export function signOut() {
  return firebaseSignOut(auth);
}

// ─── Password Reset ───────────────────────────────────────────────────────────

export function sendPasswordReset(email: string) {
  return sendPasswordResetEmail(auth, email);
}

// ─── User Document ────────────────────────────────────────────────────────────

/**
 * Creates a Firestore user document if one doesn't already exist.
 * Called on first sign-in / sign-up.
 */
export async function ensureUserDocument(
  firebaseUser: FirebaseUser,
  roles: UserRole[],
  orgId: string | null = null,
  displayName?: string,
) {
  const ref = doc(db, 'users', firebaseUser.uid);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    const nameParts = (displayName ?? firebaseUser.displayName ?? '').split(' ');
    const userData: Omit<User, 'uid'> = {
      uid: firebaseUser.uid,
      email: firebaseUser.email!,
      displayName: displayName ?? firebaseUser.displayName ?? '',
      roles,
      orgId,
      // @ts-expect-error — serverTimestamp() type differs from Timestamp at creation
      createdAt: serverTimestamp(),
      // @ts-expect-error
      lastActive: serverTimestamp(),
      emailVerified: firebaseUser.emailVerified,
      profile: {
        firstName: nameParts[0] ?? '',
        lastName: nameParts.slice(1).join(' ') ?? '',
      },
      notificationPrefs: {
        email: true,
        push: false,
        inApp: true,
        newAssignment: true,
        dueDateReminder: true,
        gradePosted: true,
        weeklyDigest: true,
      },
    };
    await setDoc(ref, userData);
  }
}

/**
 * Fetches the full user document from Firestore.
 */
export async function getUserDoc(uid: string): Promise<User | null> {
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) return null;
  return { uid: snap.id, ...snap.data() } as User;
}

// ─── Admin: Invite User ───────────────────────────────────────────────────────

/**
 * Invites a new user via the `inviteUser` Cloud Function.
 * The function creates the Auth account with emailVerified=true and sends the
 * password setup invite through the backend email provider.
 */
export async function createUserAsAdmin(
  email: string,
  displayName: string,
  roles: UserRole[],
  orgId: string | null,
): Promise<void> {
  const inviteUser = httpsCallable<
    { email: string; displayName: string; roles: UserRole[]; orgId: string | null },
    { uid: string; emailSent: boolean; emailProvider: 'resend'; resendEmailId?: string | null }
  >(functions, 'inviteUser');

  await inviteUser({ email, displayName, roles, orgId });
}
